export interface DLOMCalculationParams {
  BlockSize: number
  RID: string // Format: YYYY-MM
  MVAL: number
  SIC: number // 2-digit SIC code (1-99)
  // Optional user input overrides
  UISigQ1?: number
  UISigMedian?: number
  UISigQ3?: number
  UIhlifeQ1?: number
  UIhlifeMedian?: number
  UIhlifeQ3?: number
}

export interface DLOMApiResponse {
  // Global statistics (unchanged)
  SigMean: number
  SigQ1: number
  SigMedian: number
  SigQ3: number
  hlifeMean: number
  hlifeQ1: number
  hlifeMedian: number
  hlifeQ3: number
  Bool: number
  
  // PortID 1 data
  DISCAASPMat_1: number[][]
  DISCBSPMat_1: number[][]
  DISCLBPMat_1: number[][]
  DISCAASPMean_1: string
  DISCBSPMean_1: string
  DISCLBPMean_1: string
  DISCAASPMatLP_1: number[][]
  DISCBSPMatLP_1: number[][]
  DISCLBPMatLP_1: number[][]
  DISCAASPMeanLP_1: string
  DISCBSPMeanLP_1: string
  DISCLBPMeanLP_1: string
  DISCAASPMatUI_1: number[][] | null
  DISCBSPMatUI_1: number[][] | null
  DISCLBPMatUI_1: number[][] | null
  DISCAASPMatLPUI_1: number[][] | null
  DISCBSPMatLPUI_1: number[][] | null
  DISCLBPMatLPUI_1: number[][] | null
  
  // PortID 2 data
  DISCAASPMat_2: number[][]
  DISCBSPMat_2: number[][]
  DISCLBPMat_2: number[][]
  DISCAASPMean_2: string
  DISCBSPMean_2: string
  DISCLBPMean_2: string
  DISCAASPMatLP_2: number[][]
  DISCBSPMatLP_2: number[][]
  DISCLBPMatLP_2: number[][]
  DISCAASPMeanLP_2: string
  DISCBSPMeanLP_2: string
  DISCLBPMeanLP_2: string
  DISCAASPMatUI_2: number[][] | null
  DISCBSPMatUI_2: number[][] | null
  DISCLBPMatUI_2: number[][] | null
  DISCAASPMatLPUI_2: number[][] | null
  DISCBSPMatLPUI_2: number[][] | null
  DISCLBPMatLPUI_2: number[][] | null
  
  // PortID 3 data
  DISCAASPMat_3: number[][]
  DISCBSPMat_3: number[][]
  DISCLBPMat_3: number[][]
  DISCAASPMean_3: string
  DISCBSPMean_3: string
  DISCLBPMean_3: string
  DISCAASPMatLP_3: number[][]
  DISCBSPMatLP_3: number[][]
  DISCLBPMatLP_3: number[][]
  DISCAASPMeanLP_3: string
  DISCBSPMeanLP_3: string
  DISCLBPMeanLP_3: string
  DISCAASPMatUI_3: number[][] | null
  DISCBSPMatUI_3: number[][] | null
  DISCLBPMatUI_3: number[][] | null
  DISCAASPMatLPUI_3: number[][] | null
  DISCBSPMatLPUI_3: number[][] | null
  DISCLBPMatLPUI_3: number[][] | null
  
  // Portfolio identifiers
  portIds: {
    decile_portfolio: string
    sic_portfolio: string
    combination_portfolio: string
  }
}

export interface PortfolioData {
  id: number
  name: string
  description: string
  portfolioId: string
  matrices: {
    discountAASP: number[][]
    discountBSP: number[][]
    discountLBP: number[][]
    discountAASPLP: number[][]
    discountBSPLP: number[][]
    discountLBPLP: number[][]
  }
  matricesUI: {
    discountAASPUI: number[][] | null
    discountBSPUI: number[][] | null
    discountLBPUI: number[][] | null
    discountAASPLPUI: number[][] | null
    discountBSPLPUI: number[][] | null
    discountLBPLPUI: number[][] | null
  }
  means: {
    discountAASP: number
    discountBSP: number
    discountLBP: number
    discountAASPLP: number
    discountBSPLP: number
    discountLBPLP: number
  }
  hasUserInput: boolean
}

export interface ProcessedDLOMResults {
  summary: {
    baseDiscount: number
    adjustedDiscount: number
    finalValue: number
    originalValue: number
  }
  // Legacy structure for backward compatibility
  matrices: {
    discountAASP: number[][]
    discountBSP: number[][]
    discountLBP: number[][]
    discountAASPLP: number[][]
    discountBSPLP: number[][]
    discountLBPLP: number[][]
  }
  matricesUI: {
    discountAASPUI: number[][]
    discountBSPUI: number[][]
    discountLBPUI: number[][]
    discountAASPLPUI: number[][]
    discountBSPLPUI: number[][]
    discountLBPLPUI: number[][]
  }
  statistics: {
    volatility: {
      mean: number
      q1: number
      median: number
      q3: number
    }
    halfLife: {
      mean: number
      q1: number
      median: number
      q3: number
    }

  }
  means: {
    discountAASP: number
    discountBSP: number
    discountLBP: number
    discountAASPLP: number
    discountBSPLP: number
    discountLBPLP: number
  }
  // New portfolio structure
  portfolios: PortfolioData[]
}

export interface ApiError {
  type: string
  message: string
}

export interface ApiErrorResponse {
  statusCode: number
  error: ApiError
}

export interface TickerLookupParams {
  date: string // YYYY-MM format
  ticker: string
  blocksize: number // 0 < blocksize < 100
}

export interface TickerOverview {
  ticker: string
  annualizedStandardDeviation: number
  halfLife: number
  sic: string
}

export interface TickerModelResult {
  model: string
  blockage: number
  dlom: number
}

export interface TickerLookupResponse {
  overview: TickerOverview
  discounts: TickerModelResult[]
}

interface ExtendedError extends Error {
  statusCode?: number
  errorType?: string
}

interface EnhancedError extends Error {
  originalError?: Error
  statusCode?: number
  errorType?: string
}

class DLOMApiService {
  private baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL

  private async buildDefaultHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {}
    try {
      const { getCurrentUser, fetchAuthSession } = await import('aws-amplify/auth')
      await getCurrentUser()
      const session = await fetchAuthSession()
      const idToken = session.tokens?.idToken?.toString()
      const userId = session.tokens?.idToken?.payload?.sub || session.tokens?.accessToken?.payload?.sub
      if (idToken) headers["Authorization"] = `Bearer ${idToken}`
      if (userId) headers["X-User-Id"] = userId
    } catch {
      // unauthenticated; leave headers minimal
    }
    return headers
  }

  async calculateDLOM(params: DLOMCalculationParams): Promise<ProcessedDLOMResults> {
    try {
      // Validate SIC parameter
      if (!Number.isInteger(params.SIC) || params.SIC < 1 || params.SIC > 99) {
        throw new Error("SIC must be a number between 1 and 99")
      }

      const url = new URL(`${this.baseUrl}/calculate`)
      url.searchParams.append("BlockSize", params.BlockSize.toString())
      url.searchParams.append("RID", params.RID)
      url.searchParams.append("MVAL", params.MVAL.toString())
      url.searchParams.append("SIC", params.SIC.toString())

      // Append optional user-input parameters when provided
      const maybeAppend = (key: keyof DLOMCalculationParams, qp: string) => {
        const v = params[key]
        if (typeof v === 'number' && Number.isFinite(v)) {
          url.searchParams.append(qp, v.toString())
        }
      }
      maybeAppend('UISigQ1', 'UISigQ1')
      maybeAppend('UISigMedian', 'UISigMedian')
      maybeAppend('UISigQ3', 'UISigQ3')
      maybeAppend('UIhlifeQ1', 'UIhlifeQ1')
      maybeAppend('UIhlifeMedian', 'UIhlifeMedian')
      maybeAppend('UIhlifeQ3', 'UIhlifeQ3')

      console.log("[v0] Making DLOM API request:", url.toString())
      
      // Attach Cognito id token if available
      let authHeader: Record<string, string> = {}
      try {
        const { getCurrentUser, fetchAuthSession } = await import('aws-amplify/auth')
        await getCurrentUser()
        const session = await fetchAuthSession()
        const idToken = session.tokens?.idToken?.toString()
        if (idToken) {
          authHeader = { Authorization: `Bearer ${idToken}` }
        }
      } catch {
        // unauthenticated; proceed without header
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...authHeader,
        },
      })

      if (!response.ok) {
        // Try to parse error response from backend
        let errorMessage = `API request failed: ${response.status} ${response.statusText}`
        let errorType = "ApiError"
        
        try {
          const errorData = await response.json()
          if (errorData.error && errorData.error.message) {
            errorMessage = errorData.error.message
            errorType = errorData.error.type || "ApiError"
          }
        } catch {
          // If we can't parse the error response, use the default message
        }

        const error = new Error(errorMessage) as ExtendedError
        error.statusCode = response.status
        error.errorType = errorType
        throw error
      }

      const data: DLOMApiResponse = await response.json()

      return this.processApiResponse(data, params.MVAL)
    } catch (error) {
      console.error("[v0] DLOM API error:", error)
      
      // Enhance error with additional context
      if (error instanceof Error) {
        const enhancedError = new Error(`Failed to calculate DLOM: ${error.message}`) as EnhancedError
        enhancedError.originalError = error
        enhancedError.statusCode = (error as ExtendedError).statusCode || 500
        enhancedError.errorType = (error as ExtendedError).errorType || "UnknownError"
        throw enhancedError
      }
      
      throw new Error(`Failed to calculate DLOM: ${error instanceof Error ? error.message : "Unknown error"}`)
    }
  }

  private processApiResponse(data: DLOMApiResponse, originalValue: number): ProcessedDLOMResults {
    // Process portfolios
    // Helper function to check if portfolio data is available
    const hasValidMatrices = (
      aasp: number[][] | null | undefined, 
      bsp: number[][] | null | undefined, 
      lbp: number[][] | null | undefined, 
      aaspLP: number[][] | null | undefined, 
      bspLP: number[][] | null | undefined, 
      lbpLP: number[][] | null | undefined
    ) => {
      return aasp && bsp && lbp && aaspLP && bspLP && lbpLP &&
             Array.isArray(aasp) && Array.isArray(bsp) && Array.isArray(lbp) &&
             Array.isArray(aaspLP) && Array.isArray(bspLP) && Array.isArray(lbpLP)
    }

    const portfolios: PortfolioData[] = []

    // Portfolio 1 - Decile
    if (hasValidMatrices(data.DISCAASPMat_1, data.DISCBSPMat_1, data.DISCLBPMat_1, 
                        data.DISCAASPMatLP_1, data.DISCBSPMatLP_1, data.DISCLBPMatLP_1)) {
      portfolios.push({
        id: 1,
        name: "Size Decile",
        description: "Based on decile classification",
        portfolioId: data.portIds.decile_portfolio,
        matrices: {
          discountAASP: data.DISCAASPMat_1,
          discountBSP: data.DISCBSPMat_1,
          discountLBP: data.DISCLBPMat_1,
          discountAASPLP: data.DISCAASPMatLP_1,
          discountBSPLP: data.DISCBSPMatLP_1,
          discountLBPLP: data.DISCLBPMatLP_1,
        },
        matricesUI: {
          discountAASPUI: data.DISCAASPMatUI_1,
          discountBSPUI: data.DISCBSPMatUI_1,
          discountLBPUI: data.DISCLBPMatUI_1,
          discountAASPLPUI: data.DISCAASPMatLPUI_1,
          discountBSPLPUI: data.DISCBSPMatLPUI_1,
          discountLBPLPUI: data.DISCLBPMatLPUI_1,
        },
        means: {
          discountAASP: Number.parseFloat(data.DISCAASPMean_1),
          discountBSP: Number.parseFloat(data.DISCBSPMean_1),
          discountLBP: Number.parseFloat(data.DISCLBPMean_1),
          discountAASPLP: Number.parseFloat(data.DISCAASPMeanLP_1),
          discountBSPLP: Number.parseFloat(data.DISCBSPMeanLP_1),
          discountLBPLP: Number.parseFloat(data.DISCLBPMeanLP_1),
        },
        hasUserInput: this.hasValidUserInput(data.DISCAASPMatUI_1, data.DISCBSPMatUI_1, data.DISCLBPMatUI_1),
      })
    }

    // Portfolio 2 - SIC
    if (hasValidMatrices(data.DISCAASPMat_2, data.DISCBSPMat_2, data.DISCLBPMat_2, 
                        data.DISCAASPMatLP_2, data.DISCBSPMatLP_2, data.DISCLBPMatLP_2)) {
      portfolios.push({
        id: 2,
        name: "SIC",
        description: "Based on SIC code classification",
        portfolioId: data.portIds.sic_portfolio,
        matrices: {
          discountAASP: data.DISCAASPMat_2,
          discountBSP: data.DISCBSPMat_2,
          discountLBP: data.DISCLBPMat_2,
          discountAASPLP: data.DISCAASPMatLP_2,
          discountBSPLP: data.DISCBSPMatLP_2,
          discountLBPLP: data.DISCLBPMatLP_2,
        },
        matricesUI: {
          discountAASPUI: data.DISCAASPMatUI_2,
          discountBSPUI: data.DISCBSPMatUI_2,
          discountLBPUI: data.DISCLBPMatUI_2,
          discountAASPLPUI: data.DISCAASPMatLPUI_2,
          discountBSPLPUI: data.DISCBSPMatLPUI_2,
          discountLBPLPUI: data.DISCLBPMatLPUI_2,
        },
        means: {
          discountAASP: Number.parseFloat(data.DISCAASPMean_2),
          discountBSP: Number.parseFloat(data.DISCBSPMean_2),
          discountLBP: Number.parseFloat(data.DISCLBPMean_2),
          discountAASPLP: Number.parseFloat(data.DISCAASPMeanLP_2),
          discountBSPLP: Number.parseFloat(data.DISCBSPMeanLP_2),
          discountLBPLP: Number.parseFloat(data.DISCLBPMeanLP_2),
        },
        hasUserInput: this.hasValidUserInput(data.DISCAASPMatUI_2, data.DISCBSPMatUI_2, data.DISCLBPMatUI_2),
      })
    }

    // Portfolio 3 - Combination
    if (hasValidMatrices(data.DISCAASPMat_3, data.DISCBSPMat_3, data.DISCLBPMat_3, 
                        data.DISCAASPMatLP_3, data.DISCBSPMatLP_3, data.DISCLBPMatLP_3)) {
      portfolios.push({
        id: 3,
        name: "Intersection",
        description: "Based on combination of decile and SIC classifications",
        portfolioId: data.portIds.combination_portfolio,
        matrices: {
          discountAASP: data.DISCAASPMat_3,
          discountBSP: data.DISCBSPMat_3,
          discountLBP: data.DISCLBPMat_3,
          discountAASPLP: data.DISCAASPMatLP_3,
          discountBSPLP: data.DISCBSPMatLP_3,
          discountLBPLP: data.DISCLBPMatLP_3,
        },
        matricesUI: {
          discountAASPUI: data.DISCAASPMatUI_3,
          discountBSPUI: data.DISCBSPMatUI_3,
          discountLBPUI: data.DISCLBPMatUI_3,
          discountAASPLPUI: data.DISCAASPMatLPUI_3,
          discountBSPLPUI: data.DISCBSPMatLPUI_3,
          discountLBPLPUI: data.DISCLBPMatLPUI_3,
        },
        means: {
          discountAASP: Number.parseFloat(data.DISCAASPMean_3),
          discountBSP: Number.parseFloat(data.DISCBSPMean_3),
          discountLBP: Number.parseFloat(data.DISCLBPMean_3),
          discountAASPLP: Number.parseFloat(data.DISCAASPMeanLP_3),
          discountBSPLP: Number.parseFloat(data.DISCBSPMeanLP_3),
          discountLBPLP: Number.parseFloat(data.DISCLBPMeanLP_3),
        },
        hasUserInput: this.hasValidUserInput(data.DISCAASPMatUI_3, data.DISCBSPMatUI_3, data.DISCLBPMatUI_3),
      })
    }

    // Use first portfolio for legacy compatibility (or calculate average)
    const primaryPortfolio = portfolios[0]
    const baseDiscount = primaryPortfolio.means.discountAASP * 100
    const adjustedDiscount = primaryPortfolio.means.discountBSP * 100
    const finalValue = originalValue * (1 - primaryPortfolio.means.discountBSP)

    return {
      summary: {
        baseDiscount: Math.round(baseDiscount * 100) / 100,
        adjustedDiscount: Math.round(adjustedDiscount * 100) / 100,
        finalValue: Math.round(finalValue),
        originalValue,
      },
      // Legacy structure for backward compatibility
      matrices: primaryPortfolio.matrices,
      matricesUI: {
        discountAASPUI: primaryPortfolio.matricesUI.discountAASPUI || [],
        discountBSPUI: primaryPortfolio.matricesUI.discountBSPUI || [],
        discountLBPUI: primaryPortfolio.matricesUI.discountLBPUI || [],
        discountAASPLPUI: primaryPortfolio.matricesUI.discountAASPLPUI || [],
        discountBSPLPUI: primaryPortfolio.matricesUI.discountBSPLPUI || [],
        discountLBPLPUI: primaryPortfolio.matricesUI.discountLBPLPUI || [],
      },
      statistics: {
        volatility: {
          mean: data.SigMean,
          q1: data.SigQ1,
          median: data.SigMedian,
          q3: data.SigQ3,
        },
        halfLife: {
          mean: data.hlifeMean,
          q1: data.hlifeQ1,
          median: data.hlifeMedian,
          q3: data.hlifeQ3,
        },

      },
      means: primaryPortfolio.means,
      portfolios,
    }
  }

  private hasValidUserInput(...matrices: (number[][] | null)[]): boolean {
    return matrices.every(mat => 
      mat !== null && 
      Array.isArray(mat) && 
      mat.length === 3 && 
      mat.every(row => 
        Array.isArray(row) && 
        row.length === 3 && 
        row.every(val => Number.isFinite(val) && val >= 0 && val <= 1)
      )
    )
  }

  // Helper method to convert date to RID format (YYYY-MM)
  formatDateToRID(date: string): string {
    const dateObj = new Date(date)
    const year = dateObj.getFullYear()
    const month = String(dateObj.getMonth() + 1).padStart(2, "0")
    return `${year}-${month}`
  }

  // Helper method to determine block size based on market value
  determineBlockSize(marketValue: number): number {
    // Based on typical DLOM calculation parameters
    if (marketValue < 1000000) return 2
    if (marketValue < 10000000) return 11
    if (marketValue < 100000000) return 50
    return 99
  }

  async lookupTicker(params: TickerLookupParams): Promise<TickerLookupResponse> {
    try {
      // Validate parameters
      if (!params.date || !params.ticker || !params.blocksize) {
        throw new Error("Missing required parameters: date, ticker, and blocksize are required")
      }

      // Validate date format (YYYY-MM)
      const dateRegex = /^\d{4}-\d{2}$/
      if (!dateRegex.test(params.date)) {
        throw new Error("Date must be in YYYY-MM format")
      }

      // Validate blocksize
      if (params.blocksize <= 0 || params.blocksize >= 100) {
        throw new Error("BlockSize must be between 0 and 100 (exclusive)")
      }

      const url = new URL(`${this.baseUrl}/tickers`)
      url.searchParams.append("date", params.date)
      url.searchParams.append("ticker", params.ticker.toUpperCase())
      url.searchParams.append("blocksize", params.blocksize.toString())

      console.log("[v0] Making ticker lookup API request:", url.toString())
      
      // Attach Cognito id token if available (same as /calculate endpoint)
      let authHeader: Record<string, string> = {}
      try {
        const { getCurrentUser, fetchAuthSession } = await import('aws-amplify/auth')
        await getCurrentUser()
        const session = await fetchAuthSession()
        const idToken = session.tokens?.idToken?.toString()
        if (idToken) {
          authHeader = { Authorization: `Bearer ${idToken}` }
        }
      } catch {
        // unauthenticated; proceed without header
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...authHeader,
        },
      })

      if (!response.ok) {
        // Try to parse error response from backend
        let errorMessage = `Ticker lookup failed: ${response.status} ${response.statusText}`
        let errorType = "ApiError"
        
        try {
          const errorData = await response.json()
          if (errorData.error && errorData.error.message) {
            errorMessage = errorData.error.message
            errorType = errorData.error.type || "ApiError"
          }
        } catch {
          // If we can't parse the error response, use the default message
        }

        const error = new Error(errorMessage) as ExtendedError
        error.statusCode = response.status
        error.errorType = errorType
        throw error
      }

      const data: TickerLookupResponse = await response.json()
      return data
      
    } catch (error) {
      console.error("[v0] Ticker lookup API error:", error)
      
      // Enhance error with additional context
      if (error instanceof Error) {
        const enhancedError = new Error(`Failed to lookup ticker: ${error.message}`) as EnhancedError
        enhancedError.originalError = error
        enhancedError.statusCode = (error as ExtendedError).statusCode || 500
        enhancedError.errorType = (error as ExtendedError).errorType || "UnknownError"
        throw enhancedError
      }
      
      throw new Error(`Failed to lookup ticker: ${error instanceof Error ? error.message : "Unknown error"}`)
    }
  }
}

export const dlomApiService = new DLOMApiService()

